// Ponte de mensagens para o retorno do OAuth da Meta quando ele cai numa janela
// sem window.opener (a Meta/Chrome pode abrir o callback em aba própria). A
// janela de origem (a que chamou window.open) escuta os canais abaixo durante o
// login (ver MetaEmbeddedSignup.handleLaunchSignup) e recebe o code mesmo
// separada. Canais usados, em ordem:
//  - BroadcastChannel (mesma origem, funciona entre abas/janelas)
//  - localStorage + evento "storage" (mesma origem, para browsers sem ref elegante)
//  - window.opener.postMessage (quando o opener existir — caminho antigo mantido,
//    também usado pelo <script> inline de public/index.html para o retorno central)
export const META_OAUTH_CHANNEL_NAME = 'sinticket-meta-oauth';
const META_OAUTH_STORAGE_PREFIX = 'sinticket:meta-oauth:';

export interface MetaOAuthCodeMessage {
  type: 'META_ES_CODE';
  code: string;
  state: string;
}

export interface MetaOAuthCancelMessage {
  type: 'META_ES_CANCEL';
  state?: string;
}

export type MetaOAuthMessage = MetaOAuthCodeMessage | MetaOAuthCancelMessage;

const isMetaOAuthMessage = (data: unknown): data is MetaOAuthMessage => {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as Record<string, unknown>;
  if (candidate.type !== 'META_ES_CODE' && candidate.type !== 'META_ES_CANCEL') return false;
  if (candidate.type === 'META_ES_CODE' && typeof candidate.code !== 'string') return false;
  return true;
};

const writeStorage = (message: MetaOAuthMessage) => {
  try {
    const key = `${META_OAUTH_STORAGE_PREFIX}${message.type}:${message.state || ''}:${Date.now()}`;
    localStorage.setItem(key, JSON.stringify(message));
    setTimeout(() => {
      try {
        localStorage.removeItem(key);
      } catch {
        // storage indisponível — ignorar
      }
    }, 1000);
  } catch {
    // storage indisponível — o BroadcastChannel já tentou
  }
};

// Publica o retorno do OAuth para a janela de origem (quem abriu o popup).
export const publishOAuthMessage = (message: MetaOAuthMessage) => {
  let channelClosed = false;
  try {
    const channel = new BroadcastChannel(META_OAUTH_CHANNEL_NAME);
    channel.postMessage(message);
    channel.close();
    channelClosed = true;
  } catch {
    // BroadcastChannel indisponível nesse navegador — segue para o opener
  }

  if (!channelClosed) {
    writeStorage(message);
  }

  try {
    if (window.opener && !window.opener.closed) {
      // Retorno central: a origem que abriu o popup está no "state" ({o, n}), como
      // no <script> de public/index.html; caso contrário devolve para a própria
      // origem. O navegador descarta a mensagem se o opener for outra origem.
      let target = window.location.origin;
      try {
        const origin = message.state && JSON.parse(message.state).o;
        if (typeof origin === 'string' && /^https:\/\/([a-z0-9-]+\.)*sinticket\.com\.br$/.test(origin)) {
          target = origin;
        }
      } catch {
        // state ausente ou inválido — devolve para a própria origem
      }
      window.opener.postMessage(message, target);
    }
  } catch {
    // Origem inválida — os canais mesmos-origin já cobriram o que era possível.
  }
};

export interface MetaOAuthCleanup {
  readonly cleanupMetaOAuthListeners: () => void;
}

// Assina os canais do retorno do OAuth enquanto o login está em andamento.
// Retorna uma função que remove tudo (chamada no cleanup do handleLaunchSignup).
export const subscribeMetaOAuth = (handler: (message: MetaOAuthMessage) => void) => {
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(META_OAUTH_CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (isMetaOAuthMessage(event.data)) handler(event.data);
    };
  } catch {
    channel = null;
  }

  const onStorage = (event: StorageEvent) => {
    if (!event.key || !event.key.startsWith(META_OAUTH_STORAGE_PREFIX)) return;
    if (event.newValue) {
      try {
        const data = JSON.parse(event.newValue) as unknown;
        if (isMetaOAuthMessage(data)) handler(data);
      } catch {
        // valor não-JSON — ignorar
      }
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    if (channel) {
      channel.onmessage = null;
      channel.close();
    }
    window.removeEventListener('storage', onStorage);
  };
};